/**
 * Template codes for WhatsApp messages
 */
export const TemplateCode = {
  WELCOME: 'WELCOME',
  MEMBERSHIP_PURCHASE: 'MEMBERSHIP_PURCHASE',
  MEMBERSHIP_RENEWAL: 'MEMBERSHIP_RENEWAL',
  MEMBERSHIP_EXPIRING: 'MEMBERSHIP_EXPIRING',
  PAYMENT_REMINDER: 'PAYMENT_REMINDER',
  BIRTHDAY: 'BIRTHDAY',
  PAYMENT_RECEIPT: 'PAYMENT_RECEIPT',
  SHARE_CATALOG: 'SHARE_CATALOG',
  SALE_PAYMENT_REMINDER: 'SALE_PAYMENT_REMINDER',
} as const;

export type TemplateCodeValue = (typeof TemplateCode)[keyof typeof TemplateCode];
export type TemplateCode = TemplateCodeValue;

/**
 * Template types
 */
export const TemplateType = {
  STATIC: 'STATIC',
  PROMPT: 'PROMPT',
} as const;

export type TemplateTypeValue = (typeof TemplateType)[keyof typeof TemplateType];
export type TemplateType = TemplateTypeValue;

/**
 * Base interface for WhatsApp template data
 */
export interface WhatsAppTemplateData {
  gymId: string;
  clientId: string;
  phoneNumber: string;
  templateCode: TemplateCode;
  variables: Record<string, any>;
  metadata?: {
    contractId?: string;
    userId?: string;
    source?: string;
    instanceName?: string;
  };
}

/**
 * Welcome message template data
 */
export interface WelcomeTemplateData
  extends Omit<WhatsAppTemplateData, 'templateCode' | 'variables'> {
  templateCode: typeof TemplateCode.WELCOME;
  variables: {
    clientName: string;
    gymName: string;
    registrationDate: string;
  };
}

/**
 * Membership purchase template data
 */
export interface MembershipPurchaseTemplateData
  extends Omit<WhatsAppTemplateData, 'templateCode' | 'variables'> {
  templateCode: typeof TemplateCode.MEMBERSHIP_PURCHASE;
  variables: {
    clientName: string;
    planName: string;
    startDate: string;
    endDate: string;
    amount: string;
    paymentFrequency: string;
  };
}

/**
 * Membership renewal template data
 */
export interface MembershipRenewalTemplateData
  extends Omit<WhatsAppTemplateData, 'templateCode' | 'variables'> {
  templateCode: typeof TemplateCode.MEMBERSHIP_RENEWAL;
  variables: {
    clientName: string;
    planName: string;
    newEndDate: string;
    amount: string;
  };
}

/**
 * Membership expiring template data
 */
export interface MembershipExpiringTemplateData
  extends Omit<WhatsAppTemplateData, 'templateCode' | 'variables'> {
  templateCode: typeof TemplateCode.MEMBERSHIP_EXPIRING;
  variables: {
    clientName: string;
    planName: string;
    expirationDate: string;
    daysRemaining: string;
  };
}

/**
 * Payment reminder template data
 */
export interface PaymentReminderTemplateData
  extends Omit<WhatsAppTemplateData, 'templateCode' | 'variables'> {
  templateCode: typeof TemplateCode.PAYMENT_REMINDER;
  variables: {
    clientName: string;
    amount: string;
    dueDate: string;
    planName: string;
  };
}

/**
 * Birthday template data
 */
export interface BirthdayTemplateData
  extends Omit<WhatsAppTemplateData, 'templateCode' | 'variables'> {
  templateCode: typeof TemplateCode.BIRTHDAY;
  variables: {
    clientName: string;
    gymName: string;
    age: string;
  };
}

/**
 * Payment receipt template data
 */
export interface PaymentReceiptTemplateData
  extends Omit<WhatsAppTemplateData, 'templateCode' | 'variables'> {
  templateCode: typeof TemplateCode.PAYMENT_RECEIPT;
  variables: {
    gymName: string;
    receiptNumber: string;
    date: string;
    clientName: string;
    items: string;
    amount: string;
    paymentMethod: string;
  };
  metadata?: {
    saleId?: string;
    userId?: string;
    source?: string;
    instanceName?: string;
  };
}

/**
 * Share catalog template data
 */
export interface ShareCatalogTemplateData
  extends Omit<WhatsAppTemplateData, 'templateCode' | 'variables'> {
  templateCode: typeof TemplateCode.SHARE_CATALOG;
  variables: {
    clientName: string;
    gymName: string;
    urlCatalog: string;
  };
}

/**
 * Sale payment reminder template data
 */
export interface SalePaymentReminderTemplateData
  extends Omit<WhatsAppTemplateData, 'templateCode' | 'variables'> {
  templateCode: typeof TemplateCode.SALE_PAYMENT_REMINDER;
  variables: {
    clientName: string;
    saleNumber: string;
    total: string;
    saleDate: string;
    daysOverdue?: string;
    items?: string;
  };
  metadata?: {
    saleId?: string;
    userId?: string;
    source?: string;
    instanceName?: string;
  };
}

/**
 * Union type for all template data types
 */
export type WhatsAppTemplateEventData =
  | WelcomeTemplateData
  | MembershipPurchaseTemplateData
  | MembershipRenewalTemplateData
  | MembershipExpiringTemplateData
  | PaymentReminderTemplateData
  | BirthdayTemplateData
  | PaymentReceiptTemplateData
  | ShareCatalogTemplateData
  | SalePaymentReminderTemplateData;

/**
 * Template metadata interface
 */
export interface TemplateMetadata {
  code: TemplateCode;
  title: string;
  description: string;
  icon: string;
  variables: string[];
  exampleValues: Record<string, string>;
}
